interface ChatMessage {
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export class ChatHistoryService {
  private static readonly STORAGE_KEY = 'footyAI_chatHistory';
  private static readonly SESSION_ID_KEY = 'footyAI_sessionId';

  static getHistoryLimit(): number {
    return parseInt(import.meta.env.VITE_CHAT_HISTORY_LIMIT || '5', 10);
  }

  static getSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(this.SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  static getHistory(): ChatMessage[] {
    try {
      const history = sessionStorage.getItem(this.STORAGE_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error retrieving chat history:', error);
      return [];
    }
  }

  static addMessage(type: 'user' | 'assistant', content: string): void {
    try {
      const history = this.getHistory();
      const newMessage: ChatMessage = {
        type,
        content,
        timestamp: new Date().toISOString()
      };

      // Add new message and limit history size
      const updatedHistory = [...history, newMessage]
        .slice(-this.getHistoryLimit());

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  }

  static formatHistoryForQuery(newQuery: string): string {
    const history = this.getHistory();
    if (history.length === 0) return '';

    // Sort messages by timestamp
    const sortedHistory = [...history].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Group messages by conversation pairs
    const conversationPairs: { user?: ChatMessage; assistant?: ChatMessage }[] = [];
    let currentPair: { user?: ChatMessage; assistant?: ChatMessage } = {};

    for (const message of sortedHistory) {
      if (message.type === 'user') {
        // Start a new pair if we have a complete pair
        if (currentPair.user) {
          conversationPairs.push(currentPair);
          currentPair = {};
        }
        currentPair.user = message;
      } else if (message.type === 'assistant') {
        if (currentPair.user) {
          currentPair.assistant = message;
          conversationPairs.push(currentPair);
          currentPair = {};
        }
      }
    }

    // Add any remaining incomplete pair
    if (currentPair.user) {
      conversationPairs.push(currentPair);
    }

    // Format conversation pairs
    const formattedMessages = conversationPairs.map(pair => {
      const messages: string[] = [];
      if (pair.user) {
        messages.push(`**user message**\n${pair.user.content}`);
      }
      if (pair.assistant) {
        messages.push(`**presenter result**\n${pair.assistant.content}`);
      }
      return messages.join('\n\n');
    });

    // Join all formatted pairs with double newlines
    return formattedMessages.join('\n\n');
  }

  static clear(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }
}